/**
 * @file Firebase Auth binding for the login and password-reset pages, plus
 * `mapFirebaseError`, the single place raw Auth errors become user copy.
 * Consumers: `LoginPage`, `PasswordResetPage`, `LinkAccountModal`.
 * Invariant: no account is created here. Administrators create accounts, and
 * the `beforeUserCreated` domain gate creates them on an allowed-domain social
 * sign-in, so this hook only ever authenticates an account that already exists.
 * Invariant: every rejection from a blocking Identity Platform function reaches
 * the client as `auth/internal-error`, so that one code covers both a transient
 * stall and a permanent refusal. `blockingFunctionReason` tells them apart.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  User,
  OAuthProvider,
  signInWithRedirect,
  linkWithCredential,
  UserCredential,
  AuthCredential,
  getRedirectResult
} from 'firebase/auth';

import { auth } from '../services/startAuth';

/**
 * Interface representing authentication error information
 * @interface AuthErrorInfo
 */
interface AuthErrorInfo {
  /** Email address associated with the error */
  email: string;
  /** ID of the provider that caused the error */
  providerId: string;
  /** Firebase error code */
  errorCode: string;
  /** Authentication credential that can be used for account linking */
  credential?: AuthCredential;
}

/**
 * Interface for the Firebase authentication hook
 * @interface UseFirebaseAuth
 */
interface UseFirebaseAuth {
  /** Loading state */
  loading: boolean;
  /** Current authenticated user or null if not authenticated */
  user: User | null;
  /** Function to log in with email and password */
  login: (email: string, password: string) => Promise<UserCredential>;
  /** Function to log out the current user */
  logout: () => Promise<void>;
  /** Function to sign in with Google using redirect */
  signInWithGoogle: () => Promise<void>;
  /** Function to sign in with Microsoft using redirect */
  signInWithMicrosoft: () => Promise<void>;
  /** Function to link accounts using email/password and a credential */
  linkAccounts: (email: string, password: string, credential: AuthCredential) => Promise<UserCredential>;
  /** Function to get the result of a redirect sign-in */
  getAuthRedirectResult: () => Promise<UserCredential | null>;
  /** Function to map Firebase Auth errors to user-friendly messages */
  mapFirebaseError: (error: any) => string;
}

/**
 * Custom hook for Firebase authentication with account linking support
 * 
 * This hook provides a comprehensive interface for handling Firebase authentication,
 * including standard email/password authentication, social provider authentication,
 * and account linking functionality.
 * 
 * @returns {UseFirebaseAuth} Authentication state and functions
 */
export default function useFirebaseAuth(): UseFirebaseAuth {
  const [loading, setLoading] = useState<boolean>(true);
  
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Authenticates a user with email and password
   * 
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<UserCredential>} Firebase user credential
   * @throws Will throw an error if authentication fails
   */
  const login = async (email: string, password: string): Promise<UserCredential> => {
    return await signInWithEmailAndPassword(auth, email, password);
  };

  /**
   * Links accounts by first signing in with email/password and then linking with the provided credential
   * 
   * @param {string} email - Email address for the account to link with
   * @param {string} password - Password for the account to link with
   * @param {AuthCredential} credential - Credential from the authentication provider to link
   * @returns {Promise<UserCredential>} Firebase user credential after linking
   * @throws Will throw an error if linking fails
   */
  const linkAccounts = async (email: string, password: string, credential: AuthCredential): Promise<UserCredential> => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return await linkWithCredential(userCredential.user, credential);
  };

  /**
   * Initiates Google sign-in using redirect
   * 
   * @returns {Promise<void>}
   */
  const signInWithGoogle = async (): Promise<void> => {
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  };

  /**
   * Handles authentication errors and extracts relevant information
   * 
   * @param {any} error - The error object from Firebase authentication
   * @returns {AuthErrorInfo | null} Extracted error information or null if not a handled error type
   */
  const handleAuthError = useCallback((error: any): AuthErrorInfo | null => {
    if (error?.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData?.email;
      const credential = OAuthProvider.credentialFromError(error);
      
      if (email && credential) {
        return {
          email,
          providerId: credential.providerId,
          errorCode: error.code,
          credential: credential
        };
      }
    }
    return null;
  }, []);

  /**
   * Creates a configured Microsoft OAuthProvider with appropriate scopes
   * 
   * @returns {OAuthProvider} Configured Microsoft authentication provider
   */
  const createMicrosoftProvider = useCallback((): OAuthProvider => {
    const provider = new OAuthProvider('microsoft.com');
    
    provider.addScope('user.read');
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');
    
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    return provider;
  }, []);

  /**
   * Initiates Microsoft sign-in using redirect
   * 
   * @returns {Promise<void>}
   */
  const signInWithMicrosoft = async (): Promise<void> => {
    const provider = createMicrosoftProvider();
    await signInWithRedirect(auth, provider);
  };

  /**
   * Gets the result of a redirect sign-in
   * 
   * @returns {Promise<UserCredential | null>} The redirect result or null if no redirect occurred
   * @throws Will throw an error if getting the redirect result fails, with additional errorInfo if available
   */
  const getAuthRedirectResult = async (): Promise<UserCredential | null> => {
    try {
      const result = await getRedirectResult(auth);
      return result;
    } catch (error: any) {
      const errorInfo = handleAuthError(error);
      if (errorInfo) {
        throw { ...error, errorInfo };
      }
      throw error;
    }
  };

  /**
   * Signs out the current user
   * 
   * @returns {Promise<void>}
   * @throws Will throw an error if logout fails
   */
  const logout = async (): Promise<void> => {
    await signOut(auth);
  };

  return { 
    loading,
    user, 
    login,
    logout,
    signInWithGoogle, 
    signInWithMicrosoft,
    linkAccounts,
    getAuthRedirectResult,
    mapFirebaseError
  };
}

/**
 * Reads the reason a blocking Identity Platform function gave for refusing a
 * sign-in, or undefined when it merely timed out or broke.
 *
 * The reason has no structured carrier. `_errorWithCustomMessage` builds the
 * error through `ErrorFactory`, whose `customData` holds only `appName`, so the
 * function's own JSON error body — `{"error":{"message":…,"status":…}}`, which
 * Identity Platform embeds verbatim inside its wrapper prose — survives only in
 * `error.message`. Locate that object and hand it to `JSON.parse`; a timeout
 * carries no JSON, so parsing fails and the caller falls back to stall copy.
 *
 * Only a `PERMISSION_DENIED` body is user copy. That is the status the domain
 * gate's `HttpsError('permission-denied', …)` sends, and it is the one case
 * whose message was written for the person reading it. Any other status is the
 * function failing rather than refusing — an uncaught throw arrives as a bare
 * `INTERNAL`, which is a retry, not an explanation — so it takes stall copy.
 */
const blockingFunctionReason = (message: unknown): string | undefined => {
  if (typeof message !== 'string') return undefined;
  const start = message.indexOf('{');
  const end = message.lastIndexOf('}');
  if (start === -1 || end < start) return undefined;
  try {
    const body = JSON.parse(message.slice(start, end + 1))?.error;
    if (body?.status !== 'PERMISSION_DENIED') return undefined;
    return typeof body.message === 'string' && body.message.length > 0 ? body.message : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Maps Firebase Auth errors to user-friendly messages.
 * @param error Error thrown by Firebase Auth
 * @returns Friendly error message
 */
export const mapFirebaseError = (error: any): string => {
  const code = error.code || error.errorInfo?.errorCode;
  switch (code) {
    case 'auth/wrong-password':
      return 'That password is incorrect.';
    case 'auth/user-not-found':
      return 'No account found for that email.';
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check your credentials and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account exists with a different sign-in method, please link your accounts.';
    case 'auth/email-already-in-use':
      return 'This email address is already in use by another account.';
    case 'auth/weak-password':
      return 'The password is too weak. Please use a stronger password.';
    case 'auth/requires-recent-login':
      return 'This operation requires re-authentication. Please log in again.';
    case 'auth/user-disabled':
      return 'This user account has been disabled.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled. Enable them in the Firebase console.';
    case 'auth/invalid-email':
      return 'The email address is not valid.';
    case 'auth/internal-error':
      return blockingFunctionReason(error.message) ?? 'The server did not respond. Try again in a moment.';
    case 'auth/network-request-failed':
      return 'Could not reach the server. Check your connection and try again.';
    case 'auth/too-many-requests':
      return 'Too many attempts from this device. Wait a few minutes, then try again.';
    default:
      return "Sorry—something went wrong. Please try again.";
  }
};